import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth, useCart, useWishlist } from '../App';
import ReservationManager, { isEditableReservation } from '../components/ReservationManager';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import CommunicationPreferences from '../components/CommunicationPreferences';
import { 
  Package, 
  User as UserIcon, 
  Mail, 
  Calendar,
  ShoppingBag,
  ArrowRight,
  Tag,
  Percent,
  Gift,
  CheckCircle2,
  MapPin,
  Plus,
  Pencil,
  Trash2,
  Phone,
  Building,
  Lock,
  Star,
  X,
  Save,
  Send,
  FileText,
  Clock,
  Award,
  TrendingUp,
  DollarSign,
  Wallet,
  Heart,
  ShoppingCart,
  ChevronDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/uploads') || url.startsWith('/api/uploads')) return `${API_URL}${url}`;
  return url;
};

const DashboardPage = () => {
  const { user, token } = useAuth();
  const { addItem } = useCart();
  const { isInWishlist, toggleWishlist, refreshWishlist } = useWishlist();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteSettings, setSiteSettings] = useState(null);
  const [pricingInfo, setPricingInfo] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(true);
  
  // Profile state
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', phone: '', company: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Address state
  const [addresses, setAddresses] = useState([]);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({
    label: 'Home',
    street: '',
    street2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
    is_default: false
  });
  const [savingAddress, setSavingAddress] = useState(false);

  // Consent forms state
  const [consentForms, setConsentForms] = useState([]);
  const [canSendConsent, setCanSendConsent] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentRecipient, setConsentRecipient] = useState({ name: '', email: '' });
  const [sendingConsent, setSendingConsent] = useState(false);

  // Affiliate state
  const [affiliateInfo, setAffiliateInfo] = useState(null);
  const [loadingAffiliate, setLoadingAffiliate] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);

  // Wishlist state
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loadingWishlist, setLoadingWishlist] = useState(true);

  // Use user from location state if available (from auth callback)
  const currentUser = location.state?.user || user;

  // Helper for authenticated fetch
  const authFetch = async (url, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...options.headers
    };
    return fetch(url, { ...options, headers,  });
  };

  useEffect(() => {
    fetch(`${API_URL}/api/site-settings`)
      .then((r) => (r.ok ? r.json() : null))
      .then((s) => s && setSiteSettings(s))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await authFetch(`${API_URL}/api/orders`);
        if (response.ok) {
          setOrders(await response.json());
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchPricingInfo = async () => {
      try {
        const response = await authFetch(`${API_URL}/api/my-pricing`);
        if (response.ok) {
          setPricingInfo(await response.json());
        }
      } catch (error) {
        console.error('Error fetching pricing info:', error);
      } finally {
        setLoadingPricing(false);
      }
    };

    const fetchProfile = async () => {
      try {
        const response = await authFetch(`${API_URL}/api/profile`);
        if (response.ok) {
          const data = await response.json();
          setProfile(data);
          setAddresses(data.addresses || []);
          setProfileForm({
            name: data.name || '',
            phone: data.phone || '',
            company: data.company || ''
          });
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchOrders();
    fetchPricingInfo();
    fetchProfile();

    // Fetch consent forms
    const fetchConsentForms = async () => {
      try {
        const response = await authFetch(`${API_URL}/api/my-consent-forms`);
        if (response.ok) {
          setConsentForms(await response.json());
          setCanSendConsent(true);
        }
      } catch (e) { console.error('Error fetching consent forms:', e); }
    };
    fetchConsentForms();

    // Fetch affiliate info
    const fetchAffiliateInfo = async () => {
      try {
        const response = await authFetch(`${API_URL}/api/my-affiliate`);
        if (response.ok) {
          const data = await response.json();
          setAffiliateInfo(data);
        }
      } catch (e) { console.error('Error fetching affiliate info:', e); }
      setLoadingAffiliate(false);
    };
    fetchAffiliateInfo();

    // Fetch wishlist
    const fetchWishlist = async () => {
      try {
        const response = await authFetch(`${API_URL}/api/wishlist`);
        if (response.ok) {
          setWishlistItems(await response.json());
        }
      } catch (e) { console.error('Error fetching wishlist:', e); }
      setLoadingWishlist(false);
    };
    fetchWishlist();
  }, [token]);

  // Profile handlers
  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const response = await authFetch(`${API_URL}/api/profile`, {
        method: 'PUT',
        body: JSON.stringify(profileForm)
      });
      
      if (response.ok) {
        toast.success('Profile updated successfully');
        setProfile(prev => ({ ...prev, ...profileForm }));
        setEditingProfile(false);
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update profile');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingProfile(false);
    }
  };

  // Password handlers
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.new.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      const response = await authFetch(`${API_URL}/api/profile/change-password`, {
        method: 'POST',
        body: JSON.stringify({
          current_password: passwordForm.current,
          new_password: passwordForm.new
        })
      });
      
      if (response.ok) {
        toast.success('Password changed successfully');
        setShowPasswordModal(false);
        setPasswordForm({ current: '', new: '', confirm: '' });
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to change password');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setChangingPassword(false);
    }
  };

  // Address handlers
  const openAddressModal = (address = null) => {
    if (address) {
      setEditingAddress(address);
      setAddressForm({
        label: address.label || 'Home',
        street: address.street || '',
        street2: address.street2 || '',
        city: address.city || '',
        state: address.state || '',
        zip_code: address.zip_code || '',
        country: address.country || 'United States',
        is_default: address.is_default || false
      });
    } else {
      setEditingAddress(null);
      setAddressForm({
        label: 'Home',
        street: '',
        street2: '',
        city: '',
        state: '',
        zip_code: '',
        country: 'United States',
        is_default: addresses.length === 0
      });
    }
    setShowAddressModal(true);
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    setSavingAddress(true);
    
    try {
      const url = editingAddress 
        ? `${API_URL}/api/addresses/${editingAddress.address_id}`
        : `${API_URL}/api/addresses`;
      
      const response = await authFetch(url, {
        method: editingAddress ? 'PUT' : 'POST',
        body: JSON.stringify(addressForm)
      });
      
      // Read the response body via .text() first (Safari is more reliable
      // with .text() than .json() when headers vary), then JSON.parse.
      // This also avoids the "Body is disturbed or locked" race that bites
      // Safari mobile when the follow-up /api/profile fetch is fired while
      // the first response stream is still in-flight.
      const rawText = await response.text();
      let body = null;
      if (rawText) {
        try { body = JSON.parse(rawText); } catch (_) { body = { detail: rawText }; }
      }

      if (!response.ok) {
        let msg = 'Failed to save address';
        if (body && body.detail) {
          if (typeof body.detail === 'string') {
            msg = body.detail;
          } else if (Array.isArray(body.detail) && body.detail[0]?.msg) {
            // Pydantic 422 — first validation error is most actionable
            msg = body.detail[0].msg + (body.detail[0].loc ? ` (${body.detail[0].loc.slice(-1)[0]})` : '');
          }
        }
        toast.error(msg);
        return;
      }

      toast.success(editingAddress ? 'Address updated' : 'Address added');
      setShowAddressModal(false);

      // Refresh addresses — wrapped so a failure here can NEVER bubble up
      // and trigger a misleading "Body is disturbed" toast on the user.
      try {
        const profileResponse = await authFetch(`${API_URL}/api/profile`);
        if (profileResponse.ok) {
          const data = await profileResponse.json();
          setAddresses(data.addresses || []);
        }
      } catch (refreshErr) {
        console.error('Address refresh failed (non-fatal):', refreshErr);
      }
    } catch (error) {
      console.error('Save address error:', error);
      toast.error(error?.message || 'Failed to save address. Please try again.');
    } finally {
      setSavingAddress(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Delete this address?')) return;
    
    try {
      const response = await authFetch(`${API_URL}/api/addresses/${addressId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Address deleted');
        setAddresses(prev => prev.filter(a => a.address_id !== addressId));
      } else {
        throw new Error('Failed to delete address');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSetDefaultAddress = async (addressId) => {
    try {
      const response = await authFetch(`${API_URL}/api/addresses/${addressId}/set-default`, {
        method: 'PUT'
      });
      
      if (response.ok) {
        toast.success('Default address updated');
        setAddresses(prev => prev.map(a => ({
          ...a,
          is_default: a.address_id === addressId
        })));
      }
    } catch (error) {
      toast.error('Failed to set default address');
    }
  };

  const handleSendConsent = async (e) => {
    e.preventDefault();
    if (!consentRecipient.name.trim() || !consentRecipient.email.trim()) return;
    setSendingConsent(true);
    try {
      const response = await authFetch(`${API_URL}/api/consent-forms/send`, {
        method: 'POST',
        body: JSON.stringify({
          recipient_name: consentRecipient.name.trim(),
          recipient_email: consentRecipient.email.trim()
        })
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`Consent form sent to ${consentRecipient.email}`);
        setShowConsentModal(false);
        setConsentRecipient({ name: '', email: '' });
        // Refresh consent forms
        const res = await authFetch(`${API_URL}/api/my-consent-forms`);
        if (res.ok) setConsentForms(await res.json());
      } else {
        toast.error(data.detail || 'Failed to send consent form');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to send consent form');
    } finally {
      setSendingConsent(false);
    }
  };

  const handleDeleteConsentForm = async (formId) => {
    if (!window.confirm('Are you sure you want to delete this consent form?')) return;
    try {
      const response = await authFetch(`${API_URL}/api/consent-forms/${formId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Consent form deleted');
        setConsentForms(prev => prev.filter(f => f.form_id !== formId));
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete');
      }
    } catch (e) {
      console.error('Error deleting consent form:', e);
      toast.error('Failed to delete consent form');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2" data-testid="dashboard-title">
            My Account
          </h1>
          <p className="text-slate-600">Manage your orders, profile, and addresses</p>
        </div>

        <Tabs defaultValue={new URLSearchParams(location.search).get('tab') || 'orders'} className="space-y-6">
          {/* Mobile-friendly tabs: horizontally scrollable bar so the page itself doesn't overflow.
              Tabs auto-shrink padding on small screens for tighter fit. */}
          <div className="-mx-4 px-4 overflow-x-auto scrollbar-thin">
            <TabsList className="bg-white border border-slate-200 inline-flex w-max min-w-full">
              <TabsTrigger value="orders" className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 whitespace-nowrap" data-testid="orders-tab">
                <Package className="h-4 w-4" />
                Orders
              </TabsTrigger>
              <TabsTrigger value="profile" className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 whitespace-nowrap" data-testid="profile-tab">
                <UserIcon className="h-4 w-4" />
                Profile
              </TabsTrigger>
              <TabsTrigger value="addresses" className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 whitespace-nowrap" data-testid="addresses-tab">
                <MapPin className="h-4 w-4" />
                Addresses
              </TabsTrigger>
              {canSendConsent && (
                <TabsTrigger value="consent" className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 whitespace-nowrap" data-testid="consent-forms-tab">
                  <FileText className="h-4 w-4" />
                  <span className="hidden sm:inline">Consent Forms</span>
                  <span className="sm:hidden">Consent</span>
                </TabsTrigger>
              )}
              {affiliateInfo?.is_affiliate && (
                <TabsTrigger value="affiliate" className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 whitespace-nowrap" data-testid="affiliate-tab">
                  <Award className="h-4 w-4" />
                  Affiliate
                </TabsTrigger>
              )}
              <TabsTrigger value="wishlist" className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 whitespace-nowrap" data-testid="wishlist-tab">
                <Heart className="h-4 w-4" />
                Wishlist {wishlistItems.length > 0 && <span className="text-xs bg-rose-100 text-rose-600 px-1.5 py-0.5 rounded-full">{wishlistItems.length}</span>}
              </TabsTrigger>
              <TabsTrigger value="preferences" className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 whitespace-nowrap" data-testid="preferences-tab">
                <Mail className="h-4 w-4" />
                Preferences
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingBag className="h-5 w-5" />
                  Order History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="bg-slate-100 rounded-lg h-20 animate-pulse"></div>
                    ))}
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-heading font-semibold text-slate-900 mb-2">No orders yet</h3>
                    <p className="text-slate-500 mb-4">Start browsing our research peptides</p>
                    <Link to="/products">
                      <Button className="bg-teal-700 hover:bg-teal-800 text-white">
                        Browse Products
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div 
                        key={order.order_id} 
                        className="border border-slate-200 rounded-xl overflow-hidden hover:border-slate-300 transition-colors"
                        data-testid={`order-${order.order_id}`}
                      >
                        {/* Order Header */}
                        <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div className="flex items-center gap-3">
                              <span className="font-mono text-sm text-slate-600">{order.order_id}</span>
                              <Badge className={
                                order.status === 'confirmed' || order.status === 'completed'
                                  ? 'bg-green-100 text-green-700'
                                  : order.status === 'processing'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-amber-100 text-amber-700'
                              }>
                                {order.status || 'Pending'}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-500">
                              {new Date(order.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                        
                        {isEditableReservation(order) && (
                          <ReservationManager order={order} authFetch={authFetch} apiUrl={API_URL} settings={siteSettings} />
                        )}

                        {/* Order Items */}
                        <div className="p-4">
                          <div className="space-y-3">
                            {order.items?.slice(0, 3).map((item, index) => (
                              <div key={index} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                    <Package className="h-5 w-5 text-slate-400" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-slate-900">
                                      {item.name || item.product_name || 'Product'}
                                    </p>
                                    <p className="text-xs text-slate-500">
                                      {item.vial_size || item.variant_size || ''} × {item.quantity}
                                    </p>
                                  </div>
                                </div>
                                <p className="text-sm font-medium text-slate-700">
                                  ${((item.price || item.variant_price || 0) * item.quantity).toFixed(2)}
                                </p>
                              </div>
                            ))}
                            {order.items?.length > 3 && (
                              <p className="text-sm text-slate-500">+{order.items.length - 3} more items</p>
                            )}
                          </div>
                          
                          {/* Order Total */}
                          <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between items-center">
                            <span className="text-sm text-slate-600">{order.items?.length || 0} item(s)</span>
                            <p className="text-lg font-bold text-slate-900">${(order.total || 0).toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Profile Information Card */}
              <Card className="border-slate-100">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <UserIcon className="h-5 w-5" />
                    Profile Information
                  </CardTitle>
                  {!editingProfile && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setEditingProfile(true)}
                      data-testid="edit-profile-btn"
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {loadingProfile ? (
                    <div className="space-y-4">
                      <div className="bg-slate-100 rounded-lg h-20 animate-pulse"></div>
                      <div className="bg-slate-100 rounded-lg h-12 animate-pulse"></div>
                    </div>
                  ) : editingProfile ? (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="name">Full Name</Label>
                        <Input
                          id="name"
                          value={profileForm.name}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          value={profileForm.phone}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      <div>
                        <Label htmlFor="company">Company/Institution</Label>
                        <Input
                          id="company"
                          value={profileForm.company}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, company: e.target.value }))}
                          placeholder="Research Lab or Company"
                        />
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditingProfile(false);
                            setProfileForm({
                              name: profile?.name || '',
                              phone: profile?.phone || '',
                              company: profile?.company || ''
                            });
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSaveProfile}
                          disabled={savingProfile}
                          className="bg-teal-700 hover:bg-teal-800 text-white"
                        >
                          {savingProfile ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Avatar */}
                      <div className="flex items-center gap-4">
                        {currentUser?.picture ? (
                          <img 
                            src={currentUser.picture} 
                            alt={currentUser.name}
                            className="w-16 h-16 rounded-full"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center">
                            <UserIcon className="h-8 w-8 text-teal-700" />
                          </div>
                        )}
                        <div>
                          <h3 className="font-heading font-semibold text-slate-900" data-testid="profile-name">
                            {profile?.name || currentUser?.name || 'User'}
                          </h3>
                          <p className="text-sm text-slate-500">Research Account</p>
                        </div>
                      </div>

                      {/* Info */}
                      <div className="grid gap-3">
                        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                          <Mail className="h-5 w-5 text-slate-400" />
                          <div>
                            <p className="text-xs text-slate-500">Email</p>
                            <p className="text-sm font-medium text-slate-900">{profile?.email || currentUser?.email}</p>
                          </div>
                        </div>
                        
                        {profile?.phone && (
                          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                            <Phone className="h-5 w-5 text-slate-400" />
                            <div>
                              <p className="text-xs text-slate-500">Phone</p>
                              <p className="text-sm font-medium text-slate-900">{profile.phone}</p>
                            </div>
                          </div>
                        )}
                        
                        {profile?.company && (
                          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                            <Building className="h-5 w-5 text-slate-400" />
                            <div>
                              <p className="text-xs text-slate-500">Company</p>
                              <p className="text-sm font-medium text-slate-900">{profile.company}</p>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                          <Calendar className="h-5 w-5 text-slate-400" />
                          <div>
                            <p className="text-xs text-slate-500">Member Since</p>
                            <p className="text-sm font-medium text-slate-900">
                              {profile?.created_at 
                                ? new Date(profile.created_at).toLocaleDateString() 
                                : 'Recently joined'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Change Password */}
                      {profile?.has_password && (
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => setShowPasswordModal(true)}
                          data-testid="change-password-btn"
                        >
                          <Lock className="h-4 w-4 mr-2" />
                          Change Password
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Pricing Tier Card */}
              <Card className="border-slate-100">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Tag className="h-5 w-5" />
                    Your Pricing Tier
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingPricing ? (
                    <div className="bg-slate-100 rounded-lg h-32 animate-pulse"></div>
                  ) : pricingInfo?.tier && pricingInfo?.discount?.value > 0 ? (
                    <div className="space-y-4">
                      <div className="bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-200 rounded-xl p-6 text-center">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-teal-100 mb-3">
                          <Gift className="h-7 w-7 text-teal-600" />
                        </div>
                        <h3 className="font-heading text-xl font-bold text-teal-800 mb-1">
                          {pricingInfo.tier.name}
                        </h3>
                        <div className="flex items-center justify-center gap-2 text-teal-600">
                          <Percent className="h-5 w-5" />
                          <span className="text-2xl font-bold">
                            {pricingInfo.discount.type === 'percentage' 
                              ? `${pricingInfo.discount.value}% OFF` 
                              : `$${pricingInfo.discount.value} OFF`}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          <span>Discount automatically applied at checkout</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          <span>Priority customer support</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-slate-100 mb-3">
                        <Tag className="h-7 w-7 text-slate-400" />
                      </div>
                      <h3 className="font-heading font-semibold text-slate-900 mb-2">Standard Pricing</h3>
                      <p className="text-sm text-slate-500 mb-4">
                        Contact us to learn about special pricing tiers.
                      </p>
                      <Link to="/contact">
                        <Button variant="outline" className="border-teal-200 text-teal-700 hover:bg-teal-50">
                          Contact Us
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Addresses Tab */}
          <TabsContent value="addresses">
            <Card className="border-slate-100">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Shipping Addresses
                </CardTitle>
                <Button 
                  onClick={() => openAddressModal()} 
                  className="bg-teal-700 hover:bg-teal-800 text-white"
                  data-testid="add-address-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Address
                </Button>
              </CardHeader>
              <CardContent>
                {addresses.length === 0 ? (
                  <div className="text-center py-12">
                    <MapPin className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-heading font-semibold text-slate-900 mb-2">No addresses saved</h3>
                    <p className="text-slate-500 mb-4">Add a shipping address for faster checkout</p>
                    <Button 
                      onClick={() => openAddressModal()}
                      className="bg-teal-700 hover:bg-teal-800 text-white"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Your First Address
                    </Button>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {addresses.map((address) => (
                      <div 
                        key={address.address_id}
                        className={`relative p-4 rounded-xl border-2 ${
                          address.is_default 
                            ? 'border-teal-500 bg-teal-50/50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`address-${address.address_id}`}
                      >
                        {address.is_default && (
                          <Badge className="absolute -top-2 -right-2 bg-teal-600 text-white">
                            <Star className="h-3 w-3 mr-1" />
                            Default
                          </Badge>
                        )}
                        
                        <div className="flex items-start justify-between mb-2">
                          <span className="inline-flex items-center px-2 py-1 bg-slate-100 rounded text-xs font-medium text-slate-600">
                            {address.label}
                          </span>
                          <div className="flex gap-1">
                            <button
                              onClick={() => openAddressModal(address)}
                              className="p-1.5 hover:bg-slate-100 rounded text-slate-500"
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteAddress(address.address_id)}
                              className="p-1.5 hover:bg-red-50 rounded text-slate-500 hover:text-red-500"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="text-sm text-slate-700 space-y-0.5">
                          <p className="font-medium">{address.street}</p>
                          {address.street2 && <p>{address.street2}</p>}
                          <p>{address.city}, {address.state} {address.zip_code}</p>
                          <p>{address.country}</p>
                        </div>
                        
                        {!address.is_default && (
                          <button
                            onClick={() => handleSetDefaultAddress(address.address_id)}
                            className="mt-3 text-xs text-teal-600 hover:text-teal-700 font-medium"
                          >
                            Set as default
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Consent Forms Tab */}
          {canSendConsent && (
            <TabsContent value="consent">
              <Card className="border-slate-100">
                <CardHeader className="border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg text-slate-900">Third-Party Consent Forms</CardTitle>
                    <Button
                      onClick={() => setShowConsentModal(true)}
                      className="bg-teal-700 hover:bg-teal-800 text-white"
                      size="sm"
                      data-testid="send-consent-btn"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Send Consent Form
                    </Button>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">
                    Send research consent forms to third-party recipients when purchasing on their behalf.
                  </p>
                </CardHeader>
                <CardContent className="p-6">
                  {consentForms.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No consent forms sent yet</p>
                      <p className="text-sm text-slate-400 mt-1">Use the button above to send a consent form to a third party</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {consentForms.map((form) => (
                        <div key={form.form_id} className="p-4 bg-white border border-slate-200 rounded-lg" data-testid={`consent-form-${form.form_id}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-slate-900">{form.recipient_name}</p>
                              <p className="text-sm text-slate-500">{form.recipient_email}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {form.status === 'signed' ? (
                                <span className="flex items-center gap-1 text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                                  <CheckCircle2 className="h-3 w-3" />
                                  Signed
                                </span>
                              ) : (
                                <>
                                  <span className="flex items-center gap-1 text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full font-medium">
                                    <Clock className="h-3 w-3" />
                                    Pending
                                  </span>
                                  <button
                                    onClick={() => handleDeleteConsentForm(form.form_id)}
                                    className="p-1 text-slate-400 hover:text-red-500 rounded"
                                    title="Delete consent form"
                                    data-testid={`delete-consent-${form.form_id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-slate-400 flex items-center gap-3">
                            <span>Sent: {new Date(form.created_at).toLocaleDateString()}</span>
                            {form.signed_at && <span>Signed: {new Date(form.signed_at).toLocaleDateString()}</span>}
                            {form.signer_name && <span>Signed by: {form.signer_name}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Affiliate Tab */}
          {affiliateInfo?.is_affiliate && (
            <TabsContent value="affiliate">
              <div className="space-y-6">
                {/* Affiliate Header Card */}
                <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-slate-50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2.5 bg-purple-100 rounded-lg">
                        <Award className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-bold text-slate-900" data-testid="affiliate-title">Affiliate Program</h3>
                        <p className="text-sm text-slate-500">Your referral code: <span className="font-mono font-bold text-purple-700" data-testid="affiliate-code">{affiliateInfo.code}</span></p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600">
                      Share your code with others. When they use it, you earn points that convert to store credit.
                      Your code gives {affiliateInfo.discount_value}{affiliateInfo.discount_type === 'percentage' ? '%' : '$'} off.
                    </p>
                  </CardContent>
                </Card>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="border-slate-100">
                    <CardContent className="p-4 text-center">
                      <div className="flex justify-center mb-2">
                        <ShoppingBag className="h-5 w-5 text-slate-400" />
                      </div>
                      <p className="text-2xl font-bold text-slate-900" data-testid="affiliate-referral-orders">{affiliateInfo.referral_orders}</p>
                      <p className="text-xs text-slate-500 mt-1">Referral Orders</p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-100">
                    <CardContent className="p-4 text-center">
                      <div className="flex justify-center mb-2">
                        <TrendingUp className="h-5 w-5 text-slate-400" />
                      </div>
                      <p className="text-2xl font-bold text-slate-900" data-testid="affiliate-referral-revenue">${affiliateInfo.referral_revenue.toFixed(2)}</p>
                      <p className="text-xs text-slate-500 mt-1">Referral Revenue</p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-100">
                    <CardContent className="p-4 text-center">
                      <div className="flex justify-center mb-2">
                        <Star className="h-5 w-5 text-teal-500" />
                      </div>
                      <p className="text-2xl font-bold text-teal-700" data-testid="affiliate-referral-points">{affiliateInfo.referral_points}</p>
                      <p className="text-xs text-slate-500 mt-1">Referral Points</p>
                    </CardContent>
                  </Card>
                  <Card className="border-blue-200 bg-blue-50">
                    <CardContent className="p-4 text-center">
                      <div className="flex justify-center mb-2">
                        <Star className="h-5 w-5 text-blue-500" />
                      </div>
                      <p className="text-2xl font-bold text-blue-700" data-testid="affiliate-purchase-points">{affiliateInfo.purchase_points}</p>
                      <p className="text-xs text-blue-600 mt-1">Purchase Points</p>
                      <p className="text-xs text-blue-400">{affiliateInfo.purchase_orders} orders / ${affiliateInfo.purchase_revenue.toFixed(2)}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Total Points & Store Credit */}
                <div className="grid grid-cols-2 gap-4">
                  <Card className="border-slate-100">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-slate-500 mb-1">Total Points (Referral + Purchase)</p>
                      <p className="text-3xl font-bold text-slate-900" data-testid="affiliate-total-points">{affiliateInfo.total_points}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-purple-200 bg-purple-50">
                    <CardContent className="p-4 text-center">
                      <div className="flex justify-center mb-1">
                        <Wallet className="h-5 w-5 text-purple-500" />
                      </div>
                      <p className="text-xs text-purple-600 mb-1">Store Credit Balance</p>
                      <p className="text-3xl font-bold text-purple-700" data-testid="affiliate-store-credit">${affiliateInfo.store_credit_balance.toFixed(2)}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Credit Summary */}
                <Card className="border-slate-100">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <DollarSign className="h-5 w-5" />
                      Credit Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-sm text-slate-600">Total Earned from Points</span>
                        <span className="font-medium text-slate-900" data-testid="affiliate-earned-credit">${affiliateInfo.earned_credit.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-sm text-slate-600">Already Paid Out</span>
                        <span className="font-medium text-slate-900" data-testid="affiliate-credits-paid">${affiliateInfo.credits_paid.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm font-medium text-slate-700">Available for Payout</span>
                        <span className="font-bold text-purple-700" data-testid="affiliate-available-credit">${affiliateInfo.available_credit.toFixed(2)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Referral Orders */}
                {affiliateInfo.recent_referral_orders.length > 0 && (
                  <Card className="border-slate-100">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Package className="h-5 w-5" />
                        Recent Referral Orders
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-1">
                        {affiliateInfo.recent_referral_orders.map((order, idx) => {
                          const isExpanded = expandedOrder === `ref-${idx}`;
                          return (
                            <div key={idx} data-testid={`referral-order-${idx}`}>
                              <button
                                onClick={() => setExpandedOrder(isExpanded ? null : `ref-${idx}`)}
                                className="w-full flex justify-between items-center py-2.5 px-2 rounded-md hover:bg-slate-50 transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                  <div className="text-left">
                                    <p className="text-sm font-medium text-slate-900">{order.customer_name || 'Customer'}</p>
                                    <p className="text-xs text-slate-500">{new Date(order.created_at).toLocaleDateString()}</p>
                                  </div>
                                </div>
                                <span className="font-medium text-slate-900">${parseFloat(order.total).toFixed(2)}</span>
                              </button>
                              {isExpanded && order.items && order.items.length > 0 && (
                                <div className="ml-8 mb-2 p-2 bg-slate-50 rounded-md">
                                  <p className="text-xs font-medium text-slate-500 mb-1">Products ordered:</p>
                                  {order.items.map((item, i) => (
                                    <div key={i} className="flex justify-between text-xs py-1 text-slate-700">
                                      <span>{item.name}{item.vial_size ? ` (${item.vial_size})` : ''}</span>
                                      <span className="text-slate-500">x{item.quantity}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Recent Purchase Orders */}
                {affiliateInfo.recent_purchase_orders.length > 0 && (
                  <Card className="border-blue-100">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <ShoppingBag className="h-5 w-5 text-blue-600" />
                        Your Purchase Orders (Earning Points)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-1">
                        {affiliateInfo.recent_purchase_orders.map((order, idx) => {
                          const isExpanded = expandedOrder === `pur-${idx}`;
                          return (
                            <div key={idx} data-testid={`purchase-order-${idx}`}>
                              <button
                                onClick={() => setExpandedOrder(isExpanded ? null : `pur-${idx}`)}
                                className="w-full flex justify-between items-center py-2.5 px-2 rounded-md hover:bg-blue-50 transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  <ChevronDown className={`h-4 w-4 text-blue-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                  <div className="text-left">
                                    <p className="text-sm font-medium text-slate-900">Your Order</p>
                                    <p className="text-xs text-slate-500">{new Date(order.created_at).toLocaleDateString()}</p>
                                  </div>
                                </div>
                                <span className="font-medium text-blue-700">${parseFloat(order.total).toFixed(2)}</span>
                              </button>
                              {isExpanded && order.items && order.items.length > 0 && (
                                <div className="ml-8 mb-2 p-2 bg-blue-50 rounded-md">
                                  <p className="text-xs font-medium text-blue-500 mb-1">Products:</p>
                                  {order.items.map((item, i) => (
                                    <div key={i} className="flex justify-between text-xs py-1 text-slate-700">
                                      <span>{item.name}{item.vial_size ? ` (${item.vial_size})` : ''}</span>
                                      <span className="text-slate-500">x{item.quantity}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
          )}

          {/* Wishlist Tab */}
          <TabsContent value="wishlist">
            <div className="space-y-4">
              {loadingWishlist ? (
                <div className="text-center py-12 text-slate-500">Loading wishlist...</div>
              ) : wishlistItems.length === 0 ? (
                <Card className="border-slate-100">
                  <CardContent className="p-12 text-center">
                    <Heart className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-heading text-lg font-semibold text-slate-700 mb-2">Your wishlist is empty</h3>
                    <p className="text-sm text-slate-500 mb-6">Browse products and tap the heart icon to save items for your next cycle.</p>
                    <Link to="/products">
                      <Button className="bg-teal-700 hover:bg-teal-800 text-white">Browse Products</Button>
                    </Link>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {wishlistItems.map((item) => {
                    const product = item.product;
                    const hasVariants = product.variants && product.variants.length > 0;
                    const startingPrice = hasVariants
                      ? Math.min(...product.variants.map(v => parseFloat(v.price || 0)))
                      : parseFloat(product.price || 0);
                    const anyInStock = hasVariants
                      ? product.variants.some(v => v.in_stock)
                      : product.in_stock;

                    return (
                      <Card key={item.product_id} className="border-slate-100 overflow-hidden group" data-testid={`wishlist-item-${item.product_id}`}>
                        <Link to={`/products/${product.slug}`}>
                          <div className="aspect-square p-3 bg-slate-50 relative">
                            <img
                              src={getImageUrl(product.image_url)}
                              alt={product.name}
                              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                            />
                            {!anyInStock && (
                              <Badge className="absolute top-2 right-2 bg-red-100 text-red-700">Out of Stock</Badge>
                            )}
                          </div>
                        </Link>
                        <CardContent className="p-4">
                          <Link to={`/products/${product.slug}`}>
                            <h3 className="font-heading font-semibold text-slate-900 mb-1 group-hover:text-teal-700 transition-colors text-sm">{product.name}</h3>
                          </Link>
                          <p className="text-xs text-slate-500 mb-2">
                            {hasVariants ? `${product.variants.length} sizes` : `${product.vial_size}`}
                          </p>
                          <p className="font-heading font-bold text-teal-700 mb-3">
                            {hasVariants ? `From $${startingPrice.toFixed(2)}` : `$${startingPrice.toFixed(2)}`}
                          </p>
                          <div className="flex gap-2">
                            <Link to={`/products/${product.slug}`} className="flex-1">
                              <Button size="sm" className="w-full bg-teal-700 hover:bg-teal-800 text-white" data-testid={`wishlist-view-${item.product_id}`}>
                                <ShoppingCart className="h-3.5 w-3.5 mr-1" /> View & Add
                              </Button>
                            </Link>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-rose-200 text-rose-500 hover:bg-rose-50"
                              onClick={async () => {
                                await toggleWishlist(item.product_id);
                                setWishlistItems(prev => prev.filter(w => w.product_id !== item.product_id));
                                toast.success('Removed from wishlist');
                              }}
                              data-testid={`wishlist-remove-${item.product_id}`}
                            >
                              <Heart className="h-3.5 w-3.5 fill-rose-500" />
                            </Button>
                          </div>
                          <p className="text-xs text-slate-400 mt-2">Added {new Date(item.added_at).toLocaleDateString()}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Communication Preferences Tab */}
          <TabsContent value="preferences">
            <Card className="border-slate-100">
              <CardContent className="p-6">
                <CommunicationPreferences />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-heading font-bold text-lg">Change Password</h2>
              <button onClick={() => setShowPasswordModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleChangePassword} className="p-4 space-y-4">
              <div>
                <Label htmlFor="current-password">Current Password</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={passwordForm.current}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, current: e.target.value }))}
                  required
                />
              </div>
              <div>
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={passwordForm.new}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, new: e.target.value }))}
                  required
                  minLength={6}
                />
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={passwordForm.confirm}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm: e.target.value }))}
                  required
                  minLength={6}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowPasswordModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={changingPassword} className="flex-1 bg-teal-700 hover:bg-teal-800 text-white">
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Address Modal */}
      {showAddressModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="font-heading font-bold text-lg">
                {editingAddress ? 'Edit Address' : 'Add New Address'}
              </h2>
              <button onClick={() => setShowAddressModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSaveAddress} className="p-4 space-y-4">
              <div>
                <Label htmlFor="label">Label</Label>
                <select
                  id="label"
                  value={addressForm.label}
                  onChange={(e) => setAddressForm(prev => ({ ...prev, label: e.target.value }))}
                  className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm"
                >
                  <option value="Home">Home</option>
                  <option value="Work">Work</option>
                  <option value="Lab">Lab</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <Label htmlFor="street">Street Address *</Label>
                <Input
                  id="street"
                  value={addressForm.street}
                  onChange={(e) => setAddressForm(prev => ({ ...prev, street: e.target.value }))}
                  placeholder="123 Main Street"
                  required
                />
              </div>
              <div>
                <Label htmlFor="street2">Apt, Suite, Unit (Optional)</Label>
                <Input
                  id="street2"
                  value={addressForm.street2}
                  onChange={(e) => setAddressForm(prev => ({ ...prev, street2: e.target.value }))}
                  placeholder="Apt 4B"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={addressForm.city}
                    onChange={(e) => setAddressForm(prev => ({ ...prev, city: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    value={addressForm.state}
                    onChange={(e) => setAddressForm(prev => ({ ...prev, state: e.target.value }))}
                    placeholder="CA"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="zip">ZIP Code *</Label>
                  <Input
                    id="zip"
                    value={addressForm.zip_code}
                    onChange={(e) => setAddressForm(prev => ({ ...prev, zip_code: e.target.value }))}
                    placeholder="90210"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={addressForm.country}
                    onChange={(e) => setAddressForm(prev => ({ ...prev, country: e.target.value }))}
                    disabled
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_default"
                  checked={addressForm.is_default}
                  onChange={(e) => setAddressForm(prev => ({ ...prev, is_default: e.target.checked }))}
                  className="rounded border-slate-300 text-teal-600"
                />
                <Label htmlFor="is_default" className="text-sm cursor-pointer">Set as default shipping address</Label>
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowAddressModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={savingAddress} className="flex-1 bg-teal-700 hover:bg-teal-800 text-white">
                  <Save className="h-4 w-4 mr-1" />
                  {savingAddress ? 'Saving...' : (editingAddress ? 'Update' : 'Save Address')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Send Consent Form Modal */}
      {showConsentModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-heading font-bold text-lg">Send Consent Form</h2>
              <button onClick={() => setShowConsentModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSendConsent} className="p-4 space-y-4">
              <p className="text-sm text-slate-500">
                Send a research consent form to a third-party recipient. They will receive an email with a link to sign the form.
              </p>
              <div>
                <Label htmlFor="recipient-name">Recipient Full Name *</Label>
                <Input
                  id="recipient-name"
                  value={consentRecipient.name}
                  onChange={(e) => setConsentRecipient(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Doe"
                  required
                  data-testid="consent-recipient-name"
                />
              </div>
              <div>
                <Label htmlFor="recipient-email">Recipient Email *</Label>
                <Input
                  id="recipient-email"
                  type="email"
                  value={consentRecipient.email}
                  onChange={(e) => setConsentRecipient(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@example.com"
                  required
                  data-testid="consent-recipient-email"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowConsentModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={sendingConsent} className="flex-1 bg-teal-700 hover:bg-teal-800 text-white" data-testid="consent-send-submit">
                  <Send className="h-4 w-4 mr-1" />
                  {sendingConsent ? 'Sending...' : 'Send Consent Form'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
